import { useEffect } from 'react'
import moment from 'moment'
import { safeLocalStorage } from '@quickmd-llc/frontend-utils'
import { useSelector } from '../store/hooks/useSelector'
import { LAST_USER_ACTION_KEY } from '../utils/inactivity'

/**
 * How often to record activity while a visit is in progress. Well under the
 * 30-minute idle window, so a single missed tick can't strand the provider.
 */
export const KEEP_ALIVE_INTERVAL_MS = 120000

/**
 * Hard stop on a single armed keep-alive.
 *
 * This hook vouches for activity the provider is not actually producing, so a stuck
 * `inVisitRoom` would suppress the HIPAA §164.312(a)(2)(iii) idle logoff origin-wide
 * and forever — a worse failure than the mid-visit logout it exists to prevent. No real
 * consultation runs four hours; past that, stop vouching and let the idle timer resume.
 * Defence in depth, not the primary control: the reducer clears `inVisitRoom` on every
 * visit-exit path.
 */
export const KEEP_ALIVE_MAX_DURATION_MS = 14400000

/**
 * Keeps a provider logged in for the duration of ANY visit.
 *
 * A provider who is listening rather than typing — the normal state during a
 * consultation — produces no mouse or keyboard events, so `AppLogout`'s 30-minute
 * idle timer fires mid-call and dispatches `{ type: LOGOUT, reason: 'inactivity-timeout' }`.
 * Production Sentry shows this happening with `inVisit: True` across several tabs at
 * once, which is the "3 visits disrupted / I lose my note" report.
 *
 * The pre-existing keep-alive lived inside two Twilio callbacks in `VisitCallRoom`
 * (`currentCall.on('accept')` and `participantConnected`) and dispatched a synthetic
 * `mousemove`. That covered phone visits only — Daily video and in-person visits had
 * no equivalent — and the synthetic event was silently lost once `AppLogout` removed
 * its listeners just before logging out.
 *
 * This keys off `waitingRoomReducer.inVisitRoom` instead of per-transport wiring, and it
 * writes `lastUserAction` directly rather than hoping a synthetic event is still being
 * listened for.
 *
 * Coverage is NOT total, and the gap is deliberate to state rather than paper over.
 * `inVisitRoom` is armed by `GET_PHONE_CALL_TOKEN_SUCCESS` (phone), by
 * `START_VISIT_VIDEO_CALL_SUCCESS` (video), and by `START_INPERSON_VISIT_SUCCESS` — but
 * nothing dispatches that last one any more: the classic `startInPersonVisit` thunk was
 * removed with the `use_provider_redesign` flag (QMD-4199), and the dashboard's in-person
 * path (`useVisitActions.ts`, `postVisitsVisitIdInperson`) dispatches nothing. So NO
 * in-person visit is protected. Fixing that means adding an arming dispatch there; it is
 * not fixed here.
 */
const selectInVisitRoom = (state: Store) => state.waitingRoomReducer?.inVisitRoom

export function useInVisitKeepAlive() {
  const inVisitRoom = useSelector(selectInVisitRoom)

  useEffect(() => {
    if (!inVisitRoom) return undefined

    const recordActivity = () => {
      safeLocalStorage.set(LAST_USER_ACTION_KEY, JSON.stringify(moment().valueOf()))
    }

    // Write once on entry: a visit joined at minute 29 of the idle window would
    // otherwise lose the race against the already-armed timer.
    recordActivity()
    const intervalId = setInterval(recordActivity, KEEP_ALIVE_INTERVAL_MS)
    const stopId = setTimeout(() => clearInterval(intervalId), KEEP_ALIVE_MAX_DURATION_MS)

    return () => {
      clearInterval(intervalId)
      clearTimeout(stopId)
    }
  }, [inVisitRoom])
}
